/**
 * DisclaimerModal & CookieModal
 *
 * Two-step gate shown before the demo starts:
 * 1. CookieModal — cookie consent (Accept / Decline)
 * 2. DisclaimerModal — confidentiality agreement (Accept / Read More)
 *
 * Each step is persisted independently in localStorage.
 * ?modal=true forces both to re-show.
 */

import { useState, useCallback, useEffect, type ReactNode } from 'react';
import { withBase } from '../../../lib/withBase';
import { getAppParams } from '../../core/appParams';

const COOKIE_STORAGE_KEY = 'mlipview-cookies-accepted';
const DISCLAIMER_STORAGE_KEY = 'mlipview-disclaimer-accepted';

function isForced(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('modal') === 'true';
  } catch {
    return false;
  }
}

function isEmbed(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('embed') === '1';
  } catch {
    return false;
  }
}

function isTestMode(): boolean {
  try {
    if ((window as any).__MLIPVIEW_TEST_MODE) return true;
    return getAppParams().noTutorial;
  } catch {
    return false;
  }
}

function hasCookieConsent(): boolean {
  if (isTestMode() || isEmbed()) return true;
  if (isForced()) return false;
  try {
    const v = localStorage.getItem(COOKIE_STORAGE_KEY);
    return v === 'accepted' || v === 'declined';
  } catch {
    return false;
  }
}

function hasDisclaimerAccepted(): boolean {
  if (isTestMode()) return true;
  if (isForced()) return false;
  try {
    return localStorage.getItem(DISCLAIMER_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

// ─── Cookie Modal ───────────────────────────────────────────────────────────

function CookieModal({ onComplete }: { onComplete: () => void }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleAccept = useCallback(() => {
    try { localStorage.setItem(COOKIE_STORAGE_KEY, 'accepted'); } catch { /* */ }
    onComplete();
  }, [onComplete]);

  const handleDecline = useCallback(() => {
    try { localStorage.setItem(COOKIE_STORAGE_KEY, 'declined'); } catch { /* */ }
    onComplete();
  }, [onComplete]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm pointer-events-auto">
      <div className="bg-[#2a2a2e] rounded-2xl max-w-lg w-full mx-4 p-8 text-white shadow-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">
          Allow the use of cookies from Meta on this browser?
        </h2>

        <p className="text-sm text-white/70 mb-8">
          To find out more about the use of cookies, see our{' '}
          <a href="https://www.meta.com/legal/privacy-policy/" target="_blank" rel="noopener" className="underline text-white/90 hover:text-white">
            Privacy Policy
          </a>{' '}
          and{' '}
          <a href="https://www.facebook.com/privacy/policies/cookies" target="_blank" rel="noopener" className="underline text-white/90 hover:text-white">
            Cookies Policy
          </a>.
        </p>

        <div className="flex justify-center gap-3">
          <button
            onClick={handleAccept}
            className="px-6 py-2 rounded-lg bg-[#4471F6] hover:bg-[#3560E0] text-white text-sm font-semibold transition-colors cursor-pointer"
          >
            Accept
          </button>
          <button
            onClick={handleDecline}
            className="px-6 py-2 rounded-lg bg-transparent border border-white/30 hover:border-white/50 text-white text-sm font-semibold transition-colors cursor-pointer"
          >
            Decline
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Disclaimer Modal ───────────────────────────────────────────────────────

function DisclaimerModal() {
  const [accepted, setAccepted] = useState(hasDisclaimerAccepted);

  useEffect(() => {
    if (accepted) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [accepted]);

  const handleAccept = useCallback(() => {
    try { localStorage.setItem(DISCLAIMER_STORAGE_KEY, '1'); } catch { /* */ }
    setAccepted(true);
    window.dispatchEvent(new Event('mlipview:disclaimer-accepted'));
  }, []);

  const handleReadMore = useCallback(() => {
    window.open(withBase('/usage.html'), '_blank', 'noopener');
  }, []);

  if (accepted) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm pointer-events-auto">
      <div className="bg-[#2a2a2e] rounded-2xl max-w-lg w-full mx-4 p-8 text-white shadow-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold text-center mb-6">
          Before we begin the demo
        </h2>

        <p className="text-sm text-white/80 text-center mb-4">
          As with all AI systems, there are inherent risks that the demo may not
          perform exactly as intended.
        </p>

        <p className="text-sm text-white/80 text-center mb-8">
          By clicking Accept, you agree to be bound by the Universal Model for
          Atoms Demo Supplemental{' '}
          <a href="https://aidemos.atmeta.com/uma/terms.html" target="_blank" rel="noopener" className="underline text-white/90 hover:text-white">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="https://aidemos.atmeta.com/uma/usage.html" target="_blank" rel="noopener" className="underline text-white/90 hover:text-white">
            Acceptable Use Policy
          </a>.
        </p>

        <div className="flex justify-center gap-3">
          <button
            onClick={handleAccept}
            className="px-6 py-2 rounded-lg bg-[#4471F6] hover:bg-[#3560E0] text-white text-sm font-semibold transition-colors cursor-pointer"
          >
            Accept
          </button>
          <button
            onClick={handleReadMore}
            className="px-6 py-2 rounded-lg bg-transparent border border-white/30 hover:border-white/50 text-white text-sm font-semibold transition-colors cursor-pointer"
          >
            Read More
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Gate ────────────────────────────────────────────────────────────────────

/**
 * DisclaimerGate
 *
 * Two-step gate: cookie consent → disclaimer → children.
 * Children only render after both steps are completed.
 */
export function CookieGate({ children }: { children: ReactNode }) {
  const [cookieDone, setCookieDone] = useState(hasCookieConsent);

  if (!cookieDone) {
    return <CookieModal onComplete={() => setCookieDone(true)} />;
  }

  return <>{children}</>;
}

export function DisclaimerGate({ children }: { children: ReactNode }) {
  const [cookieDone, setCookieDone] = useState(hasCookieConsent);
  const [disclaimerDone, setDisclaimerDone] = useState(hasDisclaimerAccepted);

  useEffect(() => {
    if (disclaimerDone) return;
    const handler = () => setDisclaimerDone(true);
    window.addEventListener('mlipview:disclaimer-accepted', handler);
    return () => window.removeEventListener('mlipview:disclaimer-accepted', handler);
  }, [disclaimerDone]);

  // Step 1: Cookie consent
  if (!cookieDone) {
    return <CookieModal onComplete={() => setCookieDone(true)} />;
  }

  // Step 2: Disclaimer
  if (!disclaimerDone) {
    return <DisclaimerModal />;
  }

  // Both done — render app
  return <>{children}</>;
}
